import { createModal, showModal, closeModal } from '../modals.js';
import { escapeHtml, escapeAttr } from '../../../../engine/core/validation.js';
import { folderEntries, folderPath, destinationFolder, storageOf } from '../../../../engine/core/vault-folders.js';
import { icon } from './icons.js';
import { t } from '../../../../shared/i18n/index.js';

/** Keep the dialog (and the user's input) open when an operation fails. */
export async function vaultDialog({ title, content, submit = t('common.save'), cancel = t('common.cancel'), danger = false, setup, onSubmit }) {
  const previous = document.activeElement;
  const id = `vault-dialog-${crypto.randomUUID()}`;
  const modal = createModal({ id, title, className: 'vault-dialog', content: `${content}<p class="vault-dialog-error" role="alert" hidden></p>`,
    buttons: [{ text: cancel, action: 'cancel', className: 'modal-btn-secondary' }, { text: submit, action: 'submit', className: danger ? 'modal-btn-danger' : 'modal-btn-primary' }] });
  const button = modal.querySelector('[data-action="submit"]');
  let busy = false;
  modal.addEventListener('click', event => {
    if (busy && !event.target.closest('.modal-body')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  modal.addEventListener('keydown', event => {
    if (busy && event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); }
    if (!busy && event.key === 'Enter' && event.target.matches('input[data-vault-name]')) {
      event.preventDefault(); button.click();
    }
  }, true);
  button.addEventListener('click', async () => {
    if (busy) return;
    const errorEl = modal.querySelector('.vault-dialog-error');
    errorEl.hidden = true;
    busy = true; button.disabled = true; button.textContent = t('vault.dialog.working');
    try {
      const result = await onSubmit(modal);
      closeModal(id, result ?? true);
    } catch (error) {
      errorEl.textContent = error.message || t('vault.dialog.failed');
      errorEl.hidden = false;
    } finally {
      busy = false; button.disabled = false; button.textContent = submit;
    }
  });
  setup?.(modal, button);
  const pending = showModal(modal);
  requestAnimationFrame(() => modal.querySelector('[data-vault-name]')?.select());
  try { return await pending; }
  finally { modal.remove(); if (previous?.isConnected) previous.focus(); }
}

export function editName({ title, value = '', label = t('common.name'), submit = t('common.save'), onSubmit }) {
  return vaultDialog({ title, submit, content: `<label class="vault-field">${escapeHtml(label)}
    <input data-vault-name value="${escapeAttr(value)}" maxlength="255" autocomplete="off" required></label>`,
    onSubmit: modal => {
      const name = modal.querySelector('input').value.trim();
      if (!name) throw new Error(t('vault.dialog.nameRequired'));
      return onSubmit(name);
    } });
}

export function confirmAction(message, { title = t('vault.dialog.confirmTitle'), submit = t('common.continue'), cancel, danger = false } = {}) {
  return vaultDialog({ title, submit, cancel, danger, content: `<p class="vault-dialog-message">${escapeHtml(message)}</p>`, onSubmit: () => true });
}

/** Shared by desktop and mobile. Folder organization never changes storage. */
export function moveToFolder({ vault, entry, isFolder = false, onMoved }) {
  let selected = isFolder ? entry.parentId : entry.folderId;
  let query = '';
  return vaultDialog({ title: t('vault.move.title', { name: entry.title || entry.name }), submit: t('vault.move.submit'),
    content: `<p class="vault-dialog-message">${escapeHtml(t(storageOf(entry) === 'local' ? 'vault.move.chooseLocal' : 'vault.move.chooseOnline'))}</p>
      <label class="vault-field"><span class="sr-only">${escapeHtml(t('vault.move.findFolder'))}</span><input data-folder-search placeholder="${escapeAttr(t('vault.move.findFolderPlaceholder'))}" autocomplete="off"></label>
      <div class="vault-destinations" aria-label="${escapeAttr(t('vault.move.destinations'))}"></div>
      <div class="vault-destination-footer"><span data-destination-path></span><button type="button" class="vault-text-btn" data-new-destination>${icon('plus')} ${escapeHtml(t('vault.folder.new'))}</button></div>
      <div class="vault-new-destination" hidden><label class="vault-field">${escapeHtml(t('vault.folder.nameLabel'))}<input data-new-folder-name maxlength="255" placeholder="${escapeAttr(t('vault.folder.new'))}"></label><button type="button" class="vault-text-btn" data-create-destination>${escapeHtml(t('vault.folder.create'))}</button></div>`,
    setup(modal, submit) {
      const list = modal.querySelector('.vault-destinations');
      const render = () => {
        const index = vault.getIndex();
        list.innerHTML = folderEntries(index.folders).filter(({ folder }) => {
          try { destinationFolder(index, entry, folder.id, { movingFolder: isFolder }); }
          catch { return false; }
          return folderPath(index.folders, folder.id).map(f => f.name).join(' / ').toLowerCase().includes(query);
        }).map(({ folder, depth }) => {
          const current = folder.id === (isFolder ? entry.parentId : entry.folderId);
          return `<button type="button" class="vault-destination ${selected === folder.id ? 'selected' : ''}" data-folder-id="${escapeAttr(folder.id)}" aria-pressed="${selected === folder.id}" style="--depth:${Math.min(depth, 8)}">
            ${icon('folder')}<span>${escapeHtml(folder.name)}</span>${current ? `<small>${escapeHtml(t('vault.move.current'))}</small>` : ''}${selected === folder.id ? '<span aria-hidden="true">✓</span>' : ''}</button>`;
        }).join('') || `<p class="vault-empty-folder">${escapeHtml(t('vault.move.noMatches'))}</p>`;
        modal.querySelector('[data-destination-path]').textContent = folderPath(index.folders, selected).map(f => f.name).join(' / ');
        submit.disabled = !selected || selected === (isFolder ? entry.parentId : entry.folderId);
      };
      list.addEventListener('click', event => {
        const row = event.target.closest('[data-folder-id]');
        if (row) { selected = row.dataset.folderId; render(); list.querySelector('.selected')?.focus(); }
      });
      modal.querySelector('[data-folder-search]').addEventListener('input', event => { query = event.target.value.trim().toLowerCase(); render(); });
      modal.querySelector('[data-new-destination]').addEventListener('click', () => {
        modal.querySelector('.vault-new-destination').hidden = false;
        modal.querySelector('[data-new-folder-name]').focus();
      });
      const create = modal.querySelector('[data-create-destination]');
      create.addEventListener('click', async () => {
        const name = modal.querySelector('[data-new-folder-name]').value.trim();
        if (!name || create.disabled) return;
        create.disabled = true; submit.disabled = true;
        try {
          destinationFolder(vault.getIndex(), entry, selected, { movingFolder: isFolder });
          selected = await vault.createFolder(name, selected);
          query = ''; modal.querySelector('[data-folder-search]').value = '';
          modal.querySelector('.vault-new-destination').hidden = true;
          render(); list.querySelector('.selected')?.scrollIntoView({ block: 'nearest' });
        } catch (error) {
          const el = modal.querySelector('.vault-dialog-error'); el.textContent = error.message; el.hidden = false;
        } finally { create.disabled = false; if (modal.isConnected) render(); }
      });
      render();
    },
    async onSubmit() {
      destinationFolder(vault.getIndex(), entry, selected, { movingFolder: isFolder });
      if (isFolder) await vault.moveFolder(entry.id, selected);
      else await vault.moveMap(entry.docId, selected);
      await onMoved?.(selected);
      return selected;
    },
  });
}
